'use client'

import type { UseFormReturn } from 'react-hook-form'

import { Active, DndContext } from '@dnd-kit/core'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import { ITravelPointQuote } from '@repo/sdk'
import { SortableItem, SortableOverlay } from '@repo/ui'
import { LocationAndDate, useDropsContext } from '@repo/ui/forms'
import { useMemo, useState } from 'react'

interface IProps {
  form: UseFormReturn<{ drops: ITravelPointQuote[] }>
  isAdmin?: boolean
}

export const LocationAndDateList = ({ form, isAdmin = false }: IProps) => {
  const { drops } = useDropsContext()
  const [active, setActive] = useState<Active | null>(null)
  const activeItem = useMemo(() => drops.find(drop => drop.order === active?.id), [active, drops])

  const stopTitle = (index: number) => {
    if (index === 0) return 'Pickup'
    else if (index === drops.length - 1) return 'Delivery'
    else return 'Stop ' + index
  }

  return (
    <DndContext
      onDragCancel={() => {
        setActive(null)
      }}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          const activeIndex = drops.findIndex(({ order }) => order === active.id)
          const overIndex = drops.findIndex(({ order }) => order === over.id)

          form.setValue('drops', arrayMove(drops, activeIndex, overIndex))
        }
        setActive(null)
      }}
      onDragStart={({ active }) => {
        setActive(active)
      }}
    >
      <div className="mb-4 ml-8">
        <h2 className="mb-4 text-lg font-bold">Route</h2>
        <div className={'relative flex flex-col gap-2'}>
          {/*@ts-ignore*/}
          <SortableContext items={drops}>
            <div className="editable-drops-progress relative flex flex-col gap-2">
              {drops.slice(0, -1).map((drop, index) => (
                <SortableItem id={drop.order} key={`${drop.id}`}>
                  <LocationAndDate
                    {...{ drop, index }}
                    addStop={index === drops.length - 2}
                    control={form.control}
                    isAdmin={isAdmin}
                    showType={index !== 0 ? index !== drops.length - 2 : false}
                    title={stopTitle(index)}
                  />
                </SortableItem>
              ))}
            </div>

            {drops.slice(-1).map(drop => (
              <SortableItem id={drop.order} key={`${drop.id}`}>
                <LocationAndDate
                  {...{ drop }}
                  addStop={false}
                  control={form.control}
                  index={drops.length - 1}
                  isAdmin={isAdmin}
                  showType={false}
                  title="Delivery"
                />
              </SortableItem>
            ))}
          </SortableContext>
        </div>
        <SortableOverlay>
          {activeItem ? (
            <SortableItem id={activeItem.order}>
              <LocationAndDate control={form.control} drop={activeItem} index={-1} title="Stop" />
            </SortableItem>
          ) : null}
        </SortableOverlay>
      </div>
    </DndContext>
  )
}
